import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  Button,
  Skeleton,
  LinearProgress,
  Drawer,
  TextField,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Target,
  UserCheck,
  TrendingUp,
  Clock,
  Filter,
  X,
  PieChart as PieIcon,
  BarChart3,
  Layers,
} from "lucide-react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  FunnelChart,
  Funnel,
  LabelList,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../../services/axiosInstance";
import SideNavbar from "../../../../components/sideNavbar";
import Header from "../../../../components/header";
import apiEndPointsConfig from "../../../../services/apiEndPoints";
import EmptyState from "../components/EmptyState";

const COLORS = ["#6366F1", "#8B5CF6", "#EC4899", "#10B981", "#F59E0B"];

const ConversionReport = () => {
  const [data, setData] = useState({
    summary: { totalLeads: 0, reached: 0, qualified: 0, reachRate: "0%", conversionRate: "0%" },
    funnelData: [],
    outcomeData: [],
    sourceData: [],
    details: [],
  });
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    source: "All",
  });

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
      };
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getConversionReport}/me`,
        { params },
      );
      setData(res?.data || {
        summary: { totalLeads: 0, reached: 0, qualified: 0, reachRate: "0%", conversionRate: "0%" },
        funnelData: [],
        outcomeData: [],
        sourceData: [],
        details: [],
      });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, token, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter details by source (client-side)
  const filteredDetails = useMemo(() => {
    if (filters.source === "All") return data.details;
    return data.details.filter((item) => (item.source || "Direct / Web") === filters.source);
  }, [data.details, filters.source]);

  // Compute stats from API response
  const stats = useMemo(() => {
    // Filter outcomeData by source if needed
    let filteredOutcome = data.outcomeData;
    let filteredSource = data.sourceData;

    if (filters.source !== "All") {
      // Recalculate from filtered details
      const outcomeMap = {};
      filteredDetails.forEach((lead) => {
        outcomeMap[lead.disposition] = (outcomeMap[lead.disposition] || 0) + 1;
      });
      filteredOutcome = Object.entries(outcomeMap)
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value);
    }

    const total = filters.source === "All" ? data.summary.totalLeads : filteredDetails.length;
    const reached = filters.source === "All" ? data.summary.reached : filteredDetails.filter(d => d.hasCalls).length;
    const qualified = filters.source === "All" ? data.summary.qualified : filteredDetails.filter(d => d.isQualified).length;
    const reachRate = total > 0 ? ((reached / total) * 100).toFixed(1) : 0;
    const conversionRate = total > 0 ? ((qualified / total) * 100).toFixed(1) : 0;

    return {
      total,
      reached,
      converted: qualified,
      reachRate,
      conversionRate,
      funnelData: [
        { value: total, name: "Total Leads", fill: "#6366F1" },
        { value: reached, name: "Reached", fill: "#8B5CF6" },
        { value: qualified, name: "Qualified", fill: "#10B981" },
      ],
      outcomeData: filteredOutcome,
      sourceData: filteredSource,
    };
  }, [data, filteredDetails, filters.source]);

  const uniqueSources = useMemo(
    () => [
      "All",
      ...new Set(data.details.map((l) => l.source || "Direct / Web")),
    ],
    [data.details],
  );

  return (
    <Box
      sx={{
        ml: { xs: 0, md: "280px" },
        minHeight: "100vh",
      }}
    >
      <SideNavbar />
      {/* <Box sx={{ display: { xs: "none", md: "initial" } }}>
        <Header title="Conversion Insights" />
      </Box> */}


      <Box component="main" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 800, color: "#1E293B" }}>
              Performance Ecosystem
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Global conversion and lead quality metrics
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Filter size={18} />}
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              borderRadius: 3,
              bgcolor: "#6366F1",
              textTransform: "none",
              px: 3,
            }}
          >
            Filters
          </Button>
        </Stack>

        {/* Empty State */}
        {!loading && stats.total === 0 ? (
          <EmptyState
            title="No Conversion Data"
            subtitle="There are no leads to analyze. Add leads to see conversion metrics."
            icon="chart"
            onRefresh={fetchData}
          />
        ) : (
          <>
            {/* Top KPIs */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <MetricCard
                title="Conversion Rate"
                value={`${stats.conversionRate}%`}
                icon={<Target />}
                color="#6366F1"
                loading={loading}
              />
              <MetricCard
                title="Qualified Leads"
                value={stats.converted}
                icon={<UserCheck />}
                color="#10B981"
                loading={loading}
              />
              <MetricCard
                title="Lead Volume"
                value={stats.total}
                icon={<TrendingUp />}
                color="#F59E0B"
                loading={loading}
              />
              <MetricCard
                title="Reach Rate"
                value={`${stats.reachRate}%`}
                icon={<Layers />}
                color="#EC4899"
                loading={loading}
              />
            </Grid>

            <Grid container spacing={3}>
              {/* Funnel Chart */}
              <Grid item xs={12} lg={4}>
                <ChartPaper
                  title="Conversion Funnel"
                  icon={<BarChart3 size={18} />}
                  loading={loading}
                >
                  <Box sx={{ height: 350, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <FunnelChart>
                        <Tooltip
                          formatter={(value, name) => [`${value} leads`, name]}
                          contentStyle={{
                            borderRadius: "8px",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Funnel
                          dataKey="value"
                          data={stats.funnelData}
                          isAnimationActive
                        >
                          <LabelList
                            position="center"
                            fill="#fff"
                            dataKey="value"
                            stroke="none"
                            style={{ fontWeight: 700, fontSize: 14 }}
                          />
                          <LabelList
                            position="right"
                            fill="#64748B"
                            dataKey="name"
                            stroke="none"
                            style={{ fontWeight: 600, fontSize: 12 }}
                            offset={10}
                          />
                        </Funnel>
                      </FunnelChart>
                    </ResponsiveContainer>
                  </Box>
                </ChartPaper>
              </Grid>

              {/* Disposition Radar Chart */}
              <Grid item xs={12} lg={8}>
                <ChartPaper
                  title="Disposition Distribution"
                  icon={<Layers size={18} />}
                  loading={loading}
                >
                  <Box sx={{ height: 350, mt: 2 }}>
                    {stats.outcomeData.length === 0 ? (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <Typography variant="body2" color="text.secondary">
                          No disposition data available
                        </Typography>
                      </Box>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart cx="50%" cy="50%" outerRadius="75%" data={stats.outcomeData}>
                          <PolarGrid stroke="#E2E8F0" />
                          <PolarAngleAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }}
                            tickFormatter={(value) => value.length > 12 ? value.substring(0, 10) + ".." : value}
                          />
                          <PolarRadiusAxis
                            angle={30}
                            domain={[0, "auto"]}
                            tick={{ fontSize: 10, fill: "#94A3B8" }}
                          />
                          <Radar
                            name="Leads"
                            dataKey="value"
                            stroke="#6366F1"
                            fill="#6366F1"
                            fillOpacity={0.5}
                            strokeWidth={2}
                            dot={{ r: 4, fill: "#6366F1" }}
                          />
                          <Tooltip
                            formatter={(value) => [`${value} leads`, "Count"]}
                            contentStyle={{
                              borderRadius: "8px",
                              border: "none",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              background: "#fff",
                            }}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    )}
                  </Box>
                </ChartPaper>
              </Grid>
            </Grid>
          </>
        )}

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          PaperProps={{ sx: { width: 340, p: 4, borderRadius: "20px 0 0 20px" } }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 4 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Analytics Filter
            </Typography>
            <IconButton onClick={() => setIsDrawerOpen(false)}>
              <X />
            </IconButton>
          </Stack>
          <Stack spacing={3}>
            <TextField
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={filters.startDate}
              onChange={(e) =>
                setFilters({ ...filters, startDate: e.target.value })
              }
            />
            <TextField
              type="date"
              label="End Date"
              InputLabelProps={{ shrink: true }}
              fullWidth
              value={filters.endDate}
              onChange={(e) =>
                setFilters({ ...filters, endDate: e.target.value })
              }
            />
            <TextField
              select
              fullWidth
              label="Origin Source"
              value={filters.source}
              onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            >
              {uniqueSources.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </TextField>
            <Divider />
            <Button
              fullWidth
              variant="contained"
              onClick={() => setIsDrawerOpen(false)}
              sx={{
                bgcolor: "#6366F1",
                py: 1.5,
                borderRadius: 3,
                fontWeight: 700,
              }}
            >
              Update Reports
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() =>
                setFilters({ startDate: "", endDate: "", source: "All" })
              }
              sx={{ color: "#64748B" }}
            >
              Reset All Filters
            </Button>
          </Stack>
        </Drawer>
      </Box>
    </Box>
  );
};

// --- Sub-components ---

const MetricCard = ({ title, value, icon, color, loading }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Paper
      component={motion.div}
      whileHover={{ y: -5 }}
      sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid #E2E8F0",
        boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
      }}
      elevation={0}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            p: 1,
            bgcolor: `${color}15`,
            color: color,
            borderRadius: "12px",
          }}
        >
          {React.cloneElement(icon, { size: 24 })}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {loading ? <Skeleton width={60} /> : value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#64748B",
              textTransform: "uppercase",
            }}
          >
            {title}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  </Grid>
);

const ChartPaper = ({ title, icon, children, loading }) => (
  <Paper
    sx={{ p: 3, borderRadius: 3, border: "1px solid #E2E8F0", height: "100%" }}
    elevation={0}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      {icon && <Box sx={{ color: "#6366F1" }}>{icon}</Box>}
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {title}
      </Typography>
    </Stack>
    {loading ? (
      <Skeleton
        variant="rectangular"
        height={350}
        sx={{
          borderRadius: 1,
          animation: "pulse 1.5s ease-in-out infinite",
          "@keyframes pulse": {
            "0%": { opacity: 0.6 },
            "50%": { opacity: 0.3 },
            "100%": { opacity: 0.6 },
          },
        }}
      />
    ) : (
      children
    )}
  </Paper>
);

export default ConversionReport;
